import React from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { IButton } from '../../../../../utils/types/contentful';
import { IColor } from '../../../utils/types/contentful';
import {
  productPackState,
  productState,
  shopifyVariantsState,
  variantState,
} from '../../../utils/recoil/product';
import { withSuspense } from '../../../utils/hocs/withSuspense';
import { sidebarState } from '../../../utils/recoil/sidebar';
import { Button } from '../../UI/Elements';
import { drawerState } from 'utils/recoil/drawer';

export interface BuyProductButton {
  buttons: IButton;
  color: IColor;
  currentVariantSelection: any;
}

const LoadingButton = () => (
  <Button
    color={{ classname: 'primary' }}
    fullWidth
    disabled
    type='contained'>
    <div className='flex justify-center items-center h-full w-full'>
      <svg
        className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
        xmlns='http://www.w3.org/2000/svg'
        fill='none'
        viewBox='0 0 24 24'>
        <circle
          className='opacity-25'
          cx='12'
          cy='12'
          r='10'
          stroke='currentColor'
          strokeWidth='4'
        />
        <path
          className='opacity-75'
          fill='currentColor'
          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
        />
      </svg>
    </div>
  </Button>
);

export const BuyProductPackSelectorButton: React.FC<BuyButtonProps> =
  withSuspense(({ buttons, color, index }) => {
    const variant = useRecoilValue(variantState);
    const product = useRecoilValue(productState);
    const shopifyVariants = useRecoilValue(shopifyVariantsState);
    const resetDrawer = useResetRecoilState(drawerState);
    const [productPack, setProductPack] = useRecoilState(productPackState);

    const fullySelectedVariant = shopifyVariants?.length === 1;
    const partiallySelectedVariant = shopifyVariants?.length > 1;
    const isSelectedVariantAvailable =
      shopifyVariants && shopifyVariants[0]?.availableForSale;

    const returnBuyButtonColor = !color ? { classname: 'primary' } : color;

    const BaseButton = (props) => (
      <Button
        label={{
          text: buttons?.chooseVariant?.text,
        }}
        type='contained'
        size='base'
        color={returnBuyButtonColor}
        {...props}
      />
    );

    const handleAddToCartClick = () => {
      if (typeof window !== 'undefined' && shopifyVariants?.length === 1) {
        const newProductPack = [...productPack];

        // Update product pack at specified index
        newProductPack[index] = { variant, product, shopifyVariants };

        setProductPack(newProductPack);
        resetDrawer();
      }
    };

    const returnBuyButtonComponent = () => {
      if (partiallySelectedVariant) {
        // Has more than one variant: can't add to cart
        return <BaseButton disabled />;
      } else if (fullySelectedVariant) {
        // Has a variant to add to cart
        if (isSelectedVariantAvailable) {
          // variant is available for sale
          return (
            <BaseButton
              label={{
                text: buttons?.addToCartButton?.text,
              }}
              icon={{ iconKey: 'HiChevronRight', color: 'white' }}
              onClick={handleAddToCartClick}
            />
          );
        }
        if (!isSelectedVariantAvailable) {
          // variant is out of stock
          return (
            <BaseButton
              label={{
                text: buttons?.notifyMeLabel?.text,
              }}
              disabled
              // onClick={handleBackInStockClick} TODO ACTIVATE BACK IN STOCK POPUP
            />
          );
        }
      } else {
        return <></>;
      }
    };

    return returnBuyButtonComponent();
  }, <LoadingButton />);
